<template>
  <div
    v-if="!pageLoading"
    class="job-architecture-summaries"
    :class="{ 'job-architecture-summaries-admin': canEdit }"
  >
    <DpplSegmentedToggle
      v-if="canEdit"
      v-model="architectureMode"
      :options="architectureModeOptions"
      aria-label="Job architecture mode"
    />
    <p
      v-if="canEdit && isEditMode"
      class="job-architecture-edit-note text-body-2 text-brandPrimary"
    >
      In edit mode, changes save immediately
    </p>
    <div
      v-else-if="canEdit"
      class="job-architecture-summaries-row text-body-2 text-textDarkest"
    >
      <template
        v-for="(item, index) in summaryItems"
        :key="item.label"
      >
        <span v-if="index > 0" class="job-architecture-summary-divider">|</span>
        <span>
          {{ item.label }}
          <span class="job-architecture-summary-value">
            {{ numberWithCommas(item.value) }}
          </span>
        </span>
      </template>
    </div>
    <template v-else>
      <h3
        v-for="item in summaryItems"
        :key="item.label"
        class="text-body-2 text-textDarkest"
      >
        {{ item.label }}
        <span class="job-architecture-summary-value">
          {{ numberWithCommas(item.value) }}
        </span>
      </h3>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useAppLoading } from '@/stores/app/loading';
import { useJobArchitecture } from '@/stores/jobArchitecture/jobArchitecture';
import { numberWithCommas } from '@/helpers/formatters';
import DpplSegmentedToggle from '@/components/datapeople/DpplSegmentedToggle.vue';

const architectureModeOptions = [
  { value: 'view', label: 'View' },
  { value: 'edit', label: 'Edit' },
];

const props = defineProps({
  totals: {
    type: Object,
    required: true,
  },
  canEdit: {
    type: Boolean,
    default: false,
  },
});

const { pageLoading } = storeToRefs(useAppLoading());

const jobArchitectureStore = useJobArchitecture();
const { isEditMode } = storeToRefs(jobArchitectureStore);
const { setEditMode } = jobArchitectureStore;

const architectureMode = computed({
  get: () => (isEditMode.value ? 'edit' : 'view'),
  set: (value) => setEditMode(value === 'edit'),
});

const summaryItems = computed(() => [
  { label: 'Groups', value: props.totals.groups },
  { label: 'Families', value: props.totals.families },
  { label: 'Job Profiles', value: props.totals.profiles },
]);
</script>

<style scoped lang="less">
.job-architecture-summaries {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-end;
  margin-left: 12px;
}

.job-architecture-summaries-admin {
  gap: 8px;
}

.job-architecture-edit-note {
  text-align: right;
}

.job-architecture-summaries-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.job-architecture-summary-divider {
  color: @stroke-dark;
}

.job-architecture-summary-value {
  color: @text-brand;
}
</style>
